import React, { useMemo, useState } from "react";
import styled from "styled-components/native";
import LabelView from "../LabelView";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { FlatList } from "react-native-gesture-handler";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "../PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { useItemDetailsCardSubSections } from "../../../hooks/useSortCustomData";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { useTranslation } from "react-i18next";
import { formatItemValue, SecondSectionWithFilteredDataTypes } from "./types";
import { useSelector } from "react-redux";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { isPair } from "@utils/product/productValidators";
import { useSectionColumnCount } from "./utils";

const SecondSectionWithFilteredData = ({
  listExpanded,
  mItem,
  productType,
  fromBulkList,
  priceDetailsDataOptions,
  editBulkItem,
  handleLinkClick,
  sectionName,
  stoneType,
  fromRequestDetail,
  isLooseParcel,
}: SecondSectionWithFilteredDataTypes) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const isCustomApp = useSelector((state) => state?.appState?.isCustomApp);
  const priceMode = userInfo?.user?.price_mode;
  const { t } = useTranslation();
  const userCurrency = useUserCurrency();
  const { deviceWidth } = useIsFoldable();
  const screenWidth = useMemo(
    () => deviceWidth - (theme.isMobile ? 24 : 32),
    [theme]
  );
  const typography = useTypographyStore();
  const [containerWidth, setContainerWidth] = useState(0);
  const { itemDetailsDataOptions, defaultRows, defaultRowsWeb } =
    useItemDetailsCardSubSections(
      productType as ProductCategory,
      priceMode,
      sectionName,
      fromBulkList,
      stoneType,
      fromRequestDetail,
      theme.isMobile
    ) || {};

  const singleColumnObject = itemDetailsDataOptions?.find(
    (_item: any) => _item?.single_column === true
  );

  const { mobileColumnCount, webColumnCount } = useSectionColumnCount(
    sectionName,
    productType,
    mItem?.sub_type
  );

  const labelKeyWidth =
    ((theme.isMobile ? screenWidth : containerWidth) /
      (theme.isMobile ? mobileColumnCount : webColumnCount)) *
    0.35;
  const valueKeyWidth =
    ((theme.isMobile ? screenWidth : containerWidth) /
      (theme.isMobile ? mobileColumnCount : webColumnCount)) *
    0.65;

  const singleColumnLabelKeyWidth =
    (theme.isMobile ? screenWidth : containerWidth) * 0.35;

  const singleColumnValueKeyWidth =
    (theme.isMobile ? screenWidth : containerWidth) * 0.65;

  const filteredData = useMemo(() => {
    return itemDetailsDataOptions?.filter((item: any) => {
      if (item.name === "discountPercent" && mItem?.is_fancy === true) {
        return false;
      }
      if (item.single_column === true) {
        return false;
      }
      if (item.name === "pair" && isPair(mItem)) {
        return true;
      } else if (item.name === "pair" && !isPair(mItem)) {
        return false;
      }
      return true;
    });
  }, [itemDetailsDataOptions, mItem]);

  const filteredDisplayData = useMemo(() => {
    return !listExpanded
      ? filteredData?.slice(
          0,
          (theme.isMobile ? defaultRows : defaultRowsWeb) * 2
        )
      : filteredData;
  }, [listExpanded, filteredData]);

  const numCols = theme.isMobile ? mobileColumnCount : webColumnCount;

  return (
    <SecondContainer
      theme={theme}
      priceMode={priceMode}
      priceDetailsDataOptions={priceDetailsDataOptions}
      onLayout={(e) => {
        const removeExtraPadding = editBulkItem ? 40 : 0;
        setContainerWidth(e.nativeEvent.layout.width - removeExtraPadding);
      }}
      isLooseParcel={isLooseParcel}
    >
      {(theme.isMobile || !!containerWidth) && (
        <FlatList
          data={filteredDisplayData}
          numColumns={numCols}
          {...(numCols > 1
            ? {
                columnWrapperStyle: {
                  marginBottom: 5,
                  justifyContent: "space-between",
                },
              }
            : {})}
          contentContainerStyle={{ margin: 5 }}
          ItemSeparatorComponent={
            numCols === 1 ? () => <View style={{ height: 5 }} /> : null
          }
          renderItem={({ item }: any) =>
            item.price_modes.includes(priceMode) &&
            (item.show_popover_data &&
            mItem?.[item?.popover_data_response_key] ? (
              <PressableView
                screenWidth={screenWidth}
                containerWidth={containerWidth}
                onPress={(e) => {
                  e.preventDefault();
                  handleLinkClick(mItem, item?.popover_data_response_key);
                }}
              >
                <LabelView
                  itemLeftText={t(`${item.label}`)}
                  showKeyTruncate={true}
                  itemRightText={formatItemValue(
                    isCustomApp,
                    item.response_key,
                    mItem,
                    userCurrency,
                    userInfo,
                    t
                  )}
                  rightPrefixTextTheme={theme["link-static"]}
                  keyTypographyStyle={typography["Minor-R"]}
                  valueTypographyStyle={typography["Minor-M"]}
                  keyWidth={labelKeyWidth}
                  valueWidth={valueKeyWidth}
                />
              </PressableView>
            ) : (
              <View
                screenWidth={screenWidth / mobileColumnCount}
                containerWidth={containerWidth / webColumnCount}
                editBulkItem={editBulkItem}
              >
                <LabelView
                  itemLeftText={t(`${item.label}`)}
                  showKeyTruncate={true}
                  itemRightText={formatItemValue(
                    isCustomApp,
                    item.response_key,
                    mItem,
                    userCurrency,
                    userInfo,
                    t
                  )}
                  keyTypographyStyle={typography["Minor-R"]}
                  valueTypographyStyle={typography["Minor-M"]}
                  keyWidth={labelKeyWidth}
                  valueWidth={valueKeyWidth}
                  cellWidth={valueKeyWidth}
                />
              </View>
            ))
          }
          keyExtractor={(item: any, index: number) => `${item?.id}-${index}`}
          ListFooterComponent={() => (
            <FooterSingleColumn
              screenWidth={screenWidth}
              containerWidth={containerWidth}
              editBulkItem={editBulkItem}
              singleColumnObject={singleColumnObject}
              isCustomApp={isCustomApp}
              mItem={mItem}
              userCurrency={userCurrency}
              userInfo={userInfo}
              t={t}
              typography={typography}
              fromBulkList={fromBulkList}
              singleColumnLabelKeyWidth={singleColumnLabelKeyWidth}
              singleColumnValueKeyWidth={singleColumnValueKeyWidth}
            />
          )}
        />
      )}
    </SecondContainer>
  );
};

const FooterSingleColumn = ({
  screenWidth,
  containerWidth,
  editBulkItem,
  singleColumnObject,
  isCustomApp,
  mItem,
  userCurrency,
  userInfo,
  t,
  typography,
  fromBulkList,
  singleColumnLabelKeyWidth,
  singleColumnValueKeyWidth,
}: any) => {
  if (!singleColumnObject) return null;

  return (
    <View
      screenWidth={screenWidth}
      containerWidth={containerWidth}
      editBulkItem={editBulkItem}
    >
      <LabelView
        itemLeftText={t(`${singleColumnObject.label}`)}
        showKeyTruncate={true}
        itemRightText={formatItemValue(
          isCustomApp,
          singleColumnObject.response_key,
          mItem,
          userCurrency,
          userInfo,
          t
        )}
        keyTypographyStyle={typography["Minor-R"]}
        valueTypographyStyle={typography["Minor-M"]}
        {...(fromBulkList
          ? {
              keyWidth: singleColumnLabelKeyWidth,
              valueWidth: singleColumnValueKeyWidth,
            }
          : {
              isFixWidth: true,
            })}
      />
    </View>
  );
};

const SecondContainer = styled.View<any>`
  height: auto;
  width: auto;
  background-color: ${({ theme }) => theme["core-02"]};
  justify-content: flex-start;
  border-bottom-left-radius: ${({
    priceMode,
    priceDetailsDataOptions,
    isLooseParcel,
  }) =>
    priceMode === PriceModes.GREEN ||
    priceDetailsDataOptions?.length === 0 ||
    isLooseParcel
      ? 8
      : 0}px;
  border-bottom-right-radius: ${({
    priceMode,
    priceDetailsDataOptions,
    isLooseParcel,
  }) =>
    priceMode === PriceModes.GREEN ||
    priceDetailsDataOptions?.length === 0 ||
    isLooseParcel
      ? 8
      : 0}px;
`;

const View = styled.View<{
  screenWidth: number;
  containerWidth: number;
}>`
  width: ${({ theme, screenWidth, containerWidth }) =>
    theme.isMobile ? screenWidth : containerWidth}px;
`;

const PressableView = styled.Pressable<{
  screenWidth: number;
  containerWidth: number;
}>`
  width: ${({ theme, screenWidth, containerWidth }) =>
    theme.isMobile
      ? screenWidth / 2
      : theme.isLargeScreen
      ? containerWidth / 3.1
      : containerWidth / 3}px;
`;

export default SecondSectionWithFilteredData;
